import { Error } from "@carbon/icons-react";
import { ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Layout from "../Layout";
import { useUserContext } from "../contexts/UserContext";
import { nullUser } from "../contexts/reducers/userReducer";
import {
  Permissions,
  Roles,
} from "../models/autogenerated-deliveryService-gql/graphql";
import LoginScreen from "../pages/login/LoginScreen";
import { isNullOrEmpty } from "../utils/helper";
import FallbackComponent from "./fallbackComponent/FallbackComponent";

interface AuthAppGatewayProps {
  requiredRoles?: Roles[];
  requiredPermissions?: Permissions[];
  fallbackComponent?: ReactNode;
  children?: ReactNode;
}

const AuthAppGateway = (props: AuthAppGatewayProps) => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const {
    requiredRoles = [],
    requiredPermissions = [],
    fallbackComponent,
    children,
  } = props;

  const [authorized, setAuthorized] = useState(false);

  const storedAccessToken = localStorage.getItem("accessToken");
  const storedRefreshToken = localStorage.getItem("refreshToken");

  // user Context has not been initialized yet but localstorage contains tokens - eg: on a page refresh
  const wasLoggedIn =
    !isNullOrEmpty(storedAccessToken) &&
    !isNullOrEmpty(storedRefreshToken) &&
    user === nullUser;

  //initial state is decided by whether the user was logged in before the page refresh
  //will be set to true when an error occurs when trying to refresh the Token
  const [newLoginRequired, setNewLoginRequired] = useState(!wasLoggedIn);

  useEffect(() => {
    const handleLogInSuccessful = () => {
      setNewLoginRequired(false);
      setAuthorized(true);
    };

    const handleLogOut = () => {
      setNewLoginRequired(true);
      setAuthorized(false);
    };

    window.addEventListener("logInSuccessful", handleLogInSuccessful);
    window.addEventListener("logOut", handleLogOut);

    return () => {
      window.removeEventListener("logInSuccessful", handleLogInSuccessful);
      window.removeEventListener("logOut", handleLogOut);
    };
  }, []);

  const roleAllowed =
    requiredRoles.length === 0 ||
    (!!user.role &&
      requiredRoles.some((requiredRole) => {
        if (requiredRole === Roles.Customer) {
          return (
            user.role === Roles.Customer ||
            user.role === Roles.SupplierAndCustomer
          );
        }

        if (requiredRole === Roles.Supplier) {
          return (
            user.role === Roles.Supplier ||
            user.role === Roles.SupplierAndCustomer
          );
        }

        return user.role === requiredRole;
      }));

  const permissionAllowed =
    requiredPermissions.length === 0 ||
    requiredPermissions.every((permission) =>
      user.permissions.includes(permission),
    );

  const isAuthorizedForRoute = roleAllowed && permissionAllowed;

  if (newLoginRequired) return <LoginScreen />;

  if (!isAuthorizedForRoute) {
    return fallbackComponent ? (
      <>{fallbackComponent}</>
    ) : (
      <FallbackComponent
        middleIcon={<Error size={32} />}
        text={t("unauthorized")}
      />
    );
  }

  if (children) return <>{children}</>;

  return <Layout authorized={authorized} />;
};

export default AuthAppGateway;
